import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { getAdminUserStats } from "../../../utils/Api";

interface DataPoint {
  date: string;
  new_users: number;
  total_users: number;
}

interface StatsResponse {
  range: string;
  interval: string;
  data: DataPoint[];
  total_users: number;
}

const TIME_RANGES = [
  { value: "1D", label: "1D" },
  { value: "1W", label: "1W" },
  { value: "1M", label: "1M" },
  { value: "3M", label: "3M" },
  { value: "6M", label: "6M" },
  { value: "1Y", label: "1Y" },
  { value: "2Y", label: "2Y" },
  { value: "5Y", label: "5Y" },
  { value: "ALL", label: "All" },
];

const formatDate = (dateStr: string, interval: string): string => {
  const date = new Date(dateStr);
  switch (interval) {
    case "hour":
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    case "day":
      return date.toLocaleDateString([], { month: "short", day: "numeric" });
    case "week":
      return date.toLocaleDateString([], { month: "short", day: "numeric" });
    case "month":
      return date.toLocaleDateString([], { month: "short", year: "2-digit" });
    default:
      return date.toLocaleDateString();
  }
};

const UserGrowthChart: React.FC = () => {
  const [selectedRange, setSelectedRange] = useState("1M");
  const [data, setData] = useState<DataPoint[]>([]);
  const [dataInterval, setDataInterval] = useState("day");
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getAdminUserStats(selectedRange);
        if (!cancelled) {
          const stats: StatsResponse = response.data;
          setData(stats.data);
          setDataInterval(stats.interval);
          setTotalUsers(stats.total_users);
        }
      } catch (err) {
        if (!cancelled) {
          setError("Failed to load user statistics");
          console.error(err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [selectedRange]);

  const formattedData = data.map((point) => ({
    ...point,
    formattedDate: formatDate(point.date, dataInterval),
  }));

  return (
    <div className="bg-white rounded shadow p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0 mb-4 sm:mb-6">
        <div>
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">
            User Growth
          </h2>
          <p className="text-xs sm:text-sm text-gray-500">
            Total users: {totalUsers.toLocaleString()}
          </p>
        </div>
        <div className="flex flex-wrap gap-1">
          {TIME_RANGES.map((range) => (
            <button
              key={range.value}
              onClick={() => setSelectedRange(range.value)}
              className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded transition-colors ${
                selectedRange === range.value
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="h-64 sm:h-80 flex items-center justify-center">
          <div className="text-gray-500">Loading...</div>
        </div>
      ) : error ? (
        <div className="h-64 sm:h-80 flex items-center justify-center">
          <div className="text-red-500">{error}</div>
        </div>
      ) : data.length === 0 ? (
        <div className="h-64 sm:h-80 flex items-center justify-center">
          <div className="text-gray-500">
            No data available for this time range
          </div>
        </div>
      ) : (
        <div className="h-64 sm:h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={formattedData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="formattedDate"
                tick={{ fontSize: 12, fill: "#6b7280" }}
                tickLine={{ stroke: "#e5e7eb" }}
                axisLine={{ stroke: "#e5e7eb" }}
              />
              <YAxis
                tick={{ fontSize: 12, fill: "#6b7280" }}
                tickLine={{ stroke: "#e5e7eb" }}
                axisLine={{ stroke: "#e5e7eb" }}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "6px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
                formatter={(value, name) => {
                  const label =
                    name === "total_users" ? "Total Users" : "New Users";
                  return [
                    typeof value === "number" ? value.toLocaleString() : value,
                    label,
                  ];
                }}
                labelFormatter={(label) => label}
              />
              <Line
                type="monotone"
                dataKey="total_users"
                stroke="#2563eb"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: "#2563eb" }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default UserGrowthChart;
